import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Link,
  Typography,
} from '@mui/material';
import {
  MailOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import AuthFormContainer from 'shared/components/AuthContainerForm';
import { useAuth } from 'shared/hooks/useAuth';
import {
  absoluteAssetUrlForStoredPath,
  fetchPublicTenantBrandingById,
  getCachedPublicBranding,
} from 'shared/api/services/publicTenantBrandingService';
import ClockLoader from 'shared/components/ClockLoader';
import {
  buildTenantResetPasswordPath,
  buildTenantLoginPath,
  getTenantIdFromWindowLocation,
  isTenantUuid,
  readRouteTenantId,
  resolveTenantPublicPathKey,
} from 'shared/utils/tenantRoute';
import { buildTenantAppPath } from 'shared/utils/tenantPath';
import { rememberTenantFromBranding } from 'shared/utils/lastTenantLogin';
import { getSessionProfile } from 'shared/utils/sessionProfileStore';
import { extractProfileUserId } from 'shared/api/sessionContext';
import { clearWorkState } from 'shared/utils/sessionWorkState';
import toast from 'react-hot-toast';

const TEXT = '#2D3442';
const MUTED = '#B8BCC6';
const SOFT_RED = '#C23636';
const NAVY = '#1F2A44';
const DEFAULT_BACKGROUND = '#F4F4F4';

const initialValues = {
  emailOrPhone: '',
  password: '',
};

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');
const isPhoneNumber = (value) =>
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(value || '');

const Login = () => {
  const { signIn, user, sessionLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId: tenantIdParam } = useParams();

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [publicBranding, setPublicBranding] = useState(null);
  const [isBrandingLoading, setIsBrandingLoading] = useState(false);

  const formLocked = isSubmitting;
  const routeTenantId =
    readRouteTenantId(tenantIdParam) ??
    getTenantIdFromWindowLocation();

  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'inactivity' || reason === 'session-expired' || reason === 'user-logout') {
      if (reason === 'inactivity') {
        toast('You were signed out due to inactivity. Sign in to continue.', {
          icon: '⏱️',
        });
      } else if (reason === 'session-expired') {
        toast('Your session expired. Sign in to continue.', { icon: '⏱️' });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const tenantName = useMemo(() => {
    const name = publicBranding?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : '';
  }, [publicBranding?.name]);

  const goAfterLogin = (profile, loginMessage) => {
    const tenantSlug = resolveTenantPublicPathKey({
      name: profile?.tenantBranding?.name || tenantName || publicBranding?.name,
      id: profile?.tenantId || publicBranding?.id,
      fallback: routeTenantId,
    });
    // Always start fresh — do not offer resume-previous-work.
    const userId = extractProfileUserId(profile);
    clearWorkState(userId);
    navigate(buildTenantAppPath('/dashboard', tenantSlug), {
      replace: true,
      state: {
        loginSuccessMessage: loginMessage || 'Signed in successfully',
      },
    });
  };

  useEffect(() => {
    if (!sessionLoading && isAuthenticated && user?.data) {
      const tenantSlug = resolveTenantPublicPathKey({
        name: user.data.tenantBranding?.name || tenantName || publicBranding?.name,
        id: user.data.tenantId || publicBranding?.id,
        fallback: routeTenantId,
      });
      clearWorkState(extractProfileUserId(user.data));
      navigate(buildTenantAppPath('/dashboard', tenantSlug), { replace: true });
    }
  }, [tenantIdParam, navigate, sessionLoading, isAuthenticated, user, tenantName, publicBranding, routeTenantId]);

  useEffect(() => {
    let active = true;

    if (!routeTenantId) {
      setPublicBranding(null);
      setIsBrandingLoading(false);
      return () => {
        active = false;
      };
    }

    const cached = getCachedPublicBranding(routeTenantId);
    if (cached) {
      setPublicBranding(cached);
      setIsBrandingLoading(false);
    } else {
      setIsBrandingLoading(true);
    }

    (async () => {
      const branding = await fetchPublicTenantBrandingById(routeTenantId);
      if (!active) return;
      if (branding) {
        setPublicBranding(branding);
        rememberTenantFromBranding(branding);
        const prettyKey = resolveTenantPublicPathKey({
          name: branding.name,
          id: branding.id,
          fallback: routeTenantId,
        });
        if (
          prettyKey &&
          isTenantUuid(routeTenantId) &&
          prettyKey.toLowerCase() !== String(routeTenantId).toLowerCase()
        ) {
          navigate(buildTenantLoginPath(prettyKey), { replace: true });
        }
      } else if (!cached) {
        setPublicBranding(null);
      }
      setIsBrandingLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [routeTenantId, navigate]);

  const pagePrimary = publicBranding?.primaryColor || NAVY;
  const pageAccent = publicBranding?.secondaryColor || SOFT_RED;
  const pageBackground = publicBranding?.backgroundColor || DEFAULT_BACKGROUND;
  const tenantPhoto = typeof publicBranding?.tenantPhoto === 'string'
    ? publicBranding.tenantPhoto.trim()
    : '';
  const tenantLogoSrc = absoluteAssetUrlForStoredPath(tenantPhoto);
  const shouldShowTenantLogo = Boolean(tenantLogoSrc);
  const resetPasswordPath = buildTenantResetPasswordPath(
    resolveTenantPublicPathKey({
      name: tenantName,
      id: publicBranding?.id,
      fallback: routeTenantId,
    })
  );
  // Do not block the form on logo download — browser caches <img src>.
  const isPageLoading = Boolean(routeTenantId) && isBrandingLoading && !publicBranding;

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (field) => (event) => {
    setValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!values.emailOrPhone.trim()) {
      nextErrors.emailOrPhone = 'Email or phone is required';
    } else if (!isEmail(values.emailOrPhone) && !isPhoneNumber(values.emailOrPhone)) {
      nextErrors.emailOrPhone = 'Enter a valid email or phone number';
    }

    if (!values.password.trim()) {
      nextErrors.password = 'Password is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    let succeeded = false;

    try {
      const emailOrPhone = values.emailOrPhone;
      const isEmailInput = isEmail(emailOrPhone);

      const result = await signIn(
        isEmailInput ? emailOrPhone : '',
        isEmailInput ? '' : emailOrPhone,
        values.password
      );

      if (result?.ok) {
        succeeded = true;
        const profile = getSessionProfile();
        goAfterLogin(profile, result.message || 'Signed in successfully');
      }
    } catch (error) {
      // handled globally
    } finally {
      if (!succeeded) setIsSubmitting(false);
    }
  };

  const fieldSx = {
    mb: { xs: 2, sm: 2.5, md: 3 },
    '& .MuiOutlinedInput-root': {
      borderRadius: { xs: '12px', sm: '14px' },
      backgroundColor: '#FFFFFF',
      fontSize: { xs: '0.92rem', sm: '0.98rem', md: '1rem' },
      color: TEXT,
      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
      '& fieldset': {
        borderColor: MUTED,
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: pagePrimary,
      },
      '&.Mui-focused fieldset': {
        borderColor: pagePrimary,
        borderWidth: '1.5px',
      },
    },
    '& .MuiOutlinedInput-input': {
      py: { xs: 1.35, sm: 1.45, md: 1.55 },
      '&::placeholder': { color: TEXT, opacity: 1 },
    },
    '& .MuiFormHelperText-root': {
      ml: 0,
      mt: 0.8,
      fontSize: { xs: '0.72rem', sm: '0.78rem' },
    },
  };

  if (isPageLoading) {
    return (
      <AuthFormContainer
        backgroundColor={DEFAULT_BACKGROUND}
        minimal
      >
        <ClockLoader message="Loading tenant branding..." height="220px" />
      </AuthFormContainer>
    );
  }

  if (routeTenantId && !publicBranding) {
    return (
      <AuthFormContainer backgroundColor={DEFAULT_BACKGROUND} minimal>
        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
            Tenant not found
          </Typography>
          <Typography sx={{ color: TEXT, mb: 2 }}>
            No tenant matches &quot;{routeTenantId}&quot;. Check the name in the URL
            (example: /datahouse).
          </Typography>
          <Button component={RouterLink} to="/login" variant="contained" sx={{ textTransform: 'none' }}>
            Go to login
          </Button>
        </Box>
      </AuthFormContainer>
    );
  }

  return (
    <>
    <AuthFormContainer
      onSubmit={handleSubmit}
      backgroundColor={pageBackground}
      primaryColor={pagePrimary}
      secondaryColor={pageAccent}
    >
      {shouldShowTenantLogo ? (
        <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, mx: 'auto', width: '100%' }}>
          <Box
            component="img"
            src={tenantLogoSrc}
            alt={tenantName ? `${tenantName} logo` : 'Tenant logo'}
            referrerPolicy="no-referrer"
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              maxHeight: 120,
              mx: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>
      ) : null}

      <Box sx={{ width: '100%', textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: pagePrimary,
            mb: 0.75,
            textTransform: 'capitalize',
          }}
        >
          {tenantName || 'Datahouse'}
        </Typography>
        <Typography sx={{ color: TEXT, fontSize: { xs: '0.88rem', sm: '0.95rem' } }}>
          Sign in to continue
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          size="small"
          name="emailOrPhone"
          value={values.emailOrPhone}
          onChange={handleChange('emailOrPhone')}
          disabled={formLocked}
          placeholder="Email ID or Phone"
          error={Boolean(errors.emailOrPhone)}
          helperText={errors.emailOrPhone}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutline sx={{ color: TEXT }} />
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          size="small"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={values.password}
          onChange={handleChange('password')}
          placeholder="Password"
          disabled={formLocked}
          error={Boolean(errors.password)}
          helperText={errors.password}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined sx={{ color: TEXT }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  onClick={handleTogglePassword}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: TEXT,
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, mb: { xs: 1, sm: 1.25 } }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Link
            component={RouterLink}
            to={resetPasswordPath}
            underline="hover"
            sx={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: pagePrimary,
              '&:hover': { color: pageAccent },
            }}
          >
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          disabled={formLocked}
          sx={{
            height: { xs: 46, sm: 50, md: 54 },
            borderRadius: '14px',
            bgcolor: pagePrimary,
            color: '#fff',
            fontWeight: 700,
            letterSpacing: 'normal',
            textTransform: 'none',
            '&:hover': { bgcolor: pageAccent },
            '&.Mui-disabled': { bgcolor: '#6F7786', color: '#fff' },
          }}
        >
          {isSubmitting ? 'Signing in…' : 'Login'}
        </Button>
      </Box>
    </AuthFormContainer>
    </>
  );
};

export default Login;